import {
  COMPANY_NAME,
  formatCents,
  multiplyCents,
  toCents,
  type Cents,
} from "@roof-hawk/core";

import { COMPANY_LOGO_URL } from "../logo";

/**
 * Print a work or material order.
 *
 * `withPricing` false is the copy that goes to the crew or the counter: they
 * need the scope and the quantities, not what the job is worth. Rendering into
 * a new window and calling print() means the browser's own "Save as PDF"
 * produces the file — no PDF library, and what is on screen is what comes out.
 */
export interface PrintableOrder {
  title: string;
  /** Label/value pairs for the header block. Empty values are dropped. */
  header: readonly (readonly [string, unknown])[];
  /**
   * Deliberately narrow. This is the copy that leaves the building, so the
   * internal working — which measurement drove a quantity — has no field to
   * arrive in. `renderOrderHtml` is tested on exactly that.
   */
  items: readonly {
    description: string;
    qty: unknown;
    unit: unknown;
    unitPrice: unknown;
  }[];
  total: Cents;
  /**
   * The order's own sales tax, shown above the total on the priced copy.
   *
   * Jonathon, 2026-09-30, on the supplier copy of MO-900148: "this should show
   * the tax". The total already included it, so the lines did not add up to
   * it. Optional because a work order carries no tax to show.
   */
  tax?: { subtotal: Cents; rate: number | null; amount: Cents };
  withPricing: boolean;
  /**
   * Absolute URL of the company mark. Absolute because the print window is
   * opened blank and written into, and a relative path there has no reliable
   * base to resolve against. Optional so `renderOrderHtml` stays pure and the
   * tests can render without one.
   */
  logoUrl?: string;
}

const escapeHtml = (value: unknown): string =>
  String(value ?? "").replace(
    /[&<>"']/g,
    (c) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      })[c] as string,
  );

export function printOrder(order: PrintableOrder): void {
  const win = window.open("", "_blank", "width=900,height=1000");
  if (!win) {
    window.alert(
      "Your browser blocked the print window. Allow pop-ups for this site.",
    );
    return;
  }

  win.document.write(
    renderOrderHtml({
      ...order,
      logoUrl: new URL(COMPANY_LOGO_URL, window.location.origin).href,
    }),
  );
  win.document.close();

  // Wait for the mark to load before printing. Calling print() straight after
  // close() raced the image and produced an unbranded page often enough to
  // matter — this is the copy that goes to a customer.
  const printIt = () => {
    win.focus();
    win.print();
  };
  if (win.document.readyState === "complete") printIt();
  else win.addEventListener("load", printIt, { once: true });
}

/** The rows under the items: the total, with subtotal and tax above it. */
function totals(order: PrintableOrder): string {
  const row = (label: string, amount: Cents, cls = "") =>
    `<tr${cls ? ` class="${cls}"` : ""}><td colspan="4">${escapeHtml(
      label,
    )}</td><td class="n">${formatCents(amount)}</td></tr>`;
  const total = row("Total", order.total, order.tax ? "total" : "");
  if (!order.tax) return total;
  const { subtotal, rate, amount } = order.tax;
  return (
    row("Subtotal", subtotal, "sub") +
    row(rate == null ? "Tax" : `Tax (${rate}%)`, amount, "sub") +
    total
  );
}

/** The printed page, as HTML. Pure, so what goes out can be asserted on. */
export function renderOrderHtml(order: PrintableOrder): string {
  const { withPricing } = order;
  const columns = withPricing ? 5 : 3;

  const header = order.header
    .filter(
      ([, value]) => value !== null && value !== undefined && value !== "",
    )
    .map(
      ([label, value]) =>
        `<tr><th>${escapeHtml(label)}</th><td>${escapeHtml(value)}</td></tr>`,
    )
    .join("");

  const rows = order.items
    .map(
      (item) => `<tr>
        <td>${escapeHtml(item.description)}</td>
        <td class="n">${escapeHtml(item.qty)}</td>
        <td>${escapeHtml(item.unit)}</td>
        ${
          withPricing
            ? `<td class="n">${formatCents(
                toCents(item.unitPrice),
              )}</td><td class="n">${formatCents(
                multiplyCents(toCents(item.unitPrice), Number(item.qty ?? 0)),
              )}</td>`
            : ""
        }
      </tr>`,
    )
    .join("");

  return `<!doctype html><html><head>
    <title>${escapeHtml(order.title)}</title>
    <style>
      body{font:14px/1.5 system-ui,sans-serif;color:#16233d;margin:32px}
      h1{font-size:22px;margin:0 0 16px}
      .brand{display:flex;align-items:center;gap:14px;margin:0 0 18px}
      .brand img{width:78px;height:78px;object-fit:contain}
      .brand .co{font-size:17px;font-weight:700;letter-spacing:.2px}
      table{border-collapse:collapse;width:100%;margin-bottom:24px}
      th,td{text-align:left;padding:7px 10px;border-bottom:1px solid #e3e8f0;vertical-align:top}
      th{color:#6b7688;font-weight:600;width:150px}
      table.items th{width:auto;background:#f5f7fa}
      .n{text-align:right}
      tfoot td{font-weight:700;border-top:2px solid #16233d}
      tfoot tr.sub td{font-weight:400;border-top:0}
      tfoot tr.sub:first-child td{border-top:2px solid #16233d}
      tfoot tr.total td{border-top:1px solid #16233d}
      @media print{body{margin:0}}
    </style></head><body>
    ${
      order.logoUrl
        ? `<div class="brand">
             <img src="${escapeHtml(order.logoUrl)}" alt="${escapeHtml(COMPANY_NAME)}">
             <span class="co">${escapeHtml(COMPANY_NAME)}</span>
           </div>`
        : ""
    }
    <h1>${escapeHtml(order.title)}</h1>
    <table>${header}</table>
    <table class="items">
      <thead><tr>
        <th>Item</th><th class="n">Qty</th><th>Unit</th>
        ${withPricing ? '<th class="n">Unit Price</th><th class="n">Amount</th>' : ""}
      </tr></thead>
      <tbody>${rows || `<tr><td colspan="${columns}">No line items.</td></tr>`}</tbody>
      ${withPricing ? `<tfoot>${totals(order)}</tfoot>` : ""}
    </table>
  </body></html>`;
}
